/**
 * The chart primitives. Hand-authored, because the whole set is four shapes.
 *
 * A charting library here would ship 60–120 KB to draw a horizontal bar, and
 * would then need theming to reach the same CSS variables these read directly —
 * so it would cost more and match less. Every colour below is `var(--…)`, which
 * is what makes these legible in both themes without a second palette.
 *
 * Rules these follow, and the reasons, because they are easy to "tidy" away:
 *
 *   - **Text never wears the data colour.** Labels and values are ink; identity
 *     comes from the coloured mark beside them. `--speaker-3` is a light
 *     orange: fine as a 10px bar, unreadable as 13px type on paper.
 *   - **Every series is directly labelled.** Colour is then never the only
 *     channel carrying identity, which matters here because the speaker palette
 *     has an adjacent pair (slots 3 and 4, dark theme) that separates by ΔE
 *     14.3 — below the 15 threshold for full-colour vision. See FINDINGS.md.
 *   - **A 2px gap in the surface colour separates touching marks**, rather than
 *     a stroke around each one. A border adds ink that is not data.
 *   - **Bars grow from one baseline and carry a 4px rounded data-end**, square
 *     where they start, so length is read from the same origin every time.
 */

import type * as React from 'react'
import { cn } from '@/lib/utils'

/* -------------------------------------------------------------------------- */
/* stat tile                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A number that is its own chart.
 *
 * Used wherever the honest form is one figure: a count of turns is not
 * improved by being drawn as a single bar. The value uses PROPORTIONAL figures
 * — `tabular-nums` gives every digit the width of a zero, which makes a
 * standalone number look gappy at display sizes; tabular is for columns.
 */
export function StatTile({
  label,
  value,
  unit,
  caption,
  tone = 'default',
  children,
}: {
  label: string
  value: React.ReactNode
  unit?: string
  caption?: React.ReactNode
  tone?: 'default' | 'absent'
  children?: React.ReactNode
}) {
  return (
    // `px-4` on a phone. The tiles stay ONE per row below `sm` rather than
    // becoming a 2x2 grid, and that is deliberate: at 375px two columns leaves
    // ~155px of text width, in which "Low is a briefing; high is a
    // conversation" wraps to five lines. The caption is the part of a stat tile
    // that makes the number mean anything, so the column that fits it wins.
    <div className="flex h-full flex-col gap-2 rounded-xl border border-line bg-surface px-4 py-4 sm:px-5">
      <p className="eyebrow">{label}</p>
      <p className="flex items-baseline gap-1">
        <span
          className={cn(
            'text-[1.75rem] leading-none font-semibold',
            tone === 'absent' ? 'text-ink-dim' : 'text-ink',
          )}
        >
          {value}
        </span>
        {unit ? <span className="text-xs font-medium text-ink-dim">{unit}</span> : null}
      </p>
      {children}
      {/* Sits directly under the value rather than pinned to the bottom of a
          stretched grid cell: pinning aligns captions across a row of equal
          tiles and opens a hole in any tile the row made taller. */}
      {caption ? (
        <p className="pt-1 text-[0.6875rem] leading-relaxed text-ink-dim">{caption}</p>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* horizontal bars                                                             */
/* -------------------------------------------------------------------------- */

export interface BarRow {
  key: string
  label: string
  /** Drives the length. */
  value: number
  /** What is printed at the tip. Separated so `5.8` can print as `5.8s`. */
  display: string
  color: string
}

/**
 * The workhorse. One bar per speaker, labelled, value at the tip.
 *
 * Chosen over a donut for talk share on purpose: a donut can only be read as
 * "roughly a third", and the interesting question here — is one person doing
 * 45% or 65% of the talking — is exactly the comparison a donut cannot answer.
 */
export function BarRows({
  rows,
  max,
  emptyNote,
}: {
  rows: BarRow[]
  /** Shared scale. Pass a fixed 100 for percentages so bars are comparable. */
  max?: number
  emptyNote?: string
}) {
  const scale = max ?? Math.max(...rows.map((row) => row.value), 1)
  const allZero = rows.every((row) => row.value === 0)

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.key} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 text-[0.8125rem] text-ink">
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
              />
              <span className="truncate">{row.label}</span>
            </span>
            <span className="shrink-0 text-[0.8125rem] tabular font-medium text-ink">
              {row.display}
            </span>
          </div>
          {/* The track is one step off the surface and hairline-quiet; the bar
              is the only thing with weight. */}
          <div className="h-2 w-full overflow-hidden rounded-sm bg-surface-2">
            <span
              className="block h-full"
              style={{
                width: `${scale > 0 ? Math.max(0, (row.value / scale) * 100) : 0}%`,
                backgroundColor: row.color,
                borderRadius: '0 4px 4px 0',
              }}
            />
          </div>
        </div>
      ))}
      {allZero && emptyNote ? <p className="text-[0.6875rem] text-ink-dim">{emptyNote}</p> : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* split bar                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One bar, split by share. The 2px gaps between segments are surface-coloured
 * — that is what separates them, not a border.
 */
export function SplitBar({
  parts,
  className,
}: {
  parts: { key: string; value: number; color: string; label: string }[]
  className?: string
}) {
  const total = parts.reduce((sum, part) => sum + part.value, 0) || 1
  return (
    <div className={cn('flex h-2.5 w-full gap-[2px] overflow-hidden', className)}>
      {parts.map((part) => (
        <span
          key={part.key}
          className="h-full first:rounded-l-sm last:rounded-r-sm"
          style={{ width: `${(part.value / total) * 100}%`, backgroundColor: part.color }}
          title={`${part.label} · ${Math.round((part.value / total) * 100)}%`}
        />
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* meter                                                                       */
/* -------------------------------------------------------------------------- */

/** A single proportion against a lighter step of the same track. */
export function Meter({ ratio, color }: { ratio: number; color: string }) {
  const clamped = Math.max(0, Math.min(1, ratio))
  return (
    <div className="h-2 w-full overflow-hidden rounded-sm bg-surface-2">
      <span
        className="block h-full"
        style={{ width: `${clamped * 100}%`, backgroundColor: color, borderRadius: '0 4px 4px 0' }}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* turn ticks                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The turns of a conversation as a strip of ticks, coloured by who took them.
 *
 * A count of turns says how many; this says what the pattern was — alternating,
 * or one person holding the floor while the other interjects twice. It is the
 * only chart here that shows ORDER, which is why it earns its space beside a
 * number that is already on screen.
 */
export function TurnTicks({
  ticks,
  onSelect,
}: {
  ticks: { key: string; color: string; label: string; startMs: number; widthRatio: number }[]
  onSelect?: (ms: number) => void
}) {
  return (
    <div className="flex h-6 w-max min-w-full items-stretch gap-[2px]">
      {ticks.map((tick) => (
        <button
          key={tick.key}
          type="button"
          onClick={onSelect ? () => onSelect(tick.startMs) : undefined}
          title={tick.label}
          className="min-w-[3px] rounded-sm transition-opacity hover:opacity-80"
          style={{ flexGrow: Math.max(tick.widthRatio, 0.02), backgroundColor: tick.color }}
        />
      ))}
    </div>
  )
}
